import { getFunctions, httpsCallable } from 'firebase/functions'
import app from './firebase'
import {
  mockTaskPriceForActiveTask,
  type ActiveTaskEntry,
} from '../components/ProfitabilityPerformance/mockData'
import type {
  GetPwfActiveTasksResponse,
  GetPwfActiveTasksSummaryResponse,
  PwfActiveTaskRaw,
  PwfActiveTasksCursor,
  PwfActiveTasksSummary,
} from '../types'
import { resolvePwfProjectDisplayFields } from '../utils/pwfProjectNumber'

export const ACTIVE_TASKS_PAGE_SIZE = 10

type ActiveTasksPageResult = {
  tasks: ActiveTaskEntry[]
  hasMore: boolean
  nextCursor: PwfActiveTasksCursor | null
  recordedAt: string
}

const inFlightPages = new Map<string, Promise<ActiveTasksPageResult>>()

export function mapPwfActiveTask(raw: PwfActiveTaskRaw): ActiveTaskEntry {
  const { projectNumber } = resolvePwfProjectDisplayFields({
    projectNumber: raw.projectNumber,
    projectTitle: raw.projectTitle,
    fallbackTitle: raw.client,
  })
  return {
    id: raw.id,
    dueDate: raw.dueDate,
    startDate: raw.startDate,
    tags: raw.tags,
    employeeName: raw.employeeName,
    projectType: raw.projectType,
    projectNumber,
    projectId: raw.projectId ?? null,
    client: raw.client,
    taskName: raw.taskName,
    timeRecordedHours: raw.timeRecordedHours,
    allocatedHours: raw.timeAllocatedHours,
    contactId: raw.contactId ?? null,
    assigneeContactIds: raw.assigneeContactIds ?? [],
    taskPrice:
      raw.taskPrice != null && Number.isFinite(raw.taskPrice)
        ? raw.taskPrice
        : mockTaskPriceForActiveTask(raw.projectType, raw.timeRecordedHours),
  }
}

function friendlyPwfError(err: unknown, fnName: string): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('not-found') || msg.includes('NOT_FOUND')) {
      return `${fnName} is not deployed yet. Run: npx firebase-tools deploy --only functions:${fnName}`
    }
    if (msg.includes('unauthenticated') || msg.includes('UNAUTHENTICATED')) {
      return 'Sign in to load ProWorkflow active tasks.'
    }
    if (msg.includes('deadline') || msg.includes('timeout') || msg.includes('504')) {
      return 'ProWorkflow request timed out. Try refreshing — the table loads in small pages.'
    }
    return msg
  }
  return 'Failed to load active tasks from ProWorkflow'
}

async function fetchActiveTasksPageFromPwfUncached(options?: {
  pageSize?: number
  cursor?: PwfActiveTasksCursor | null
}): Promise<ActiveTasksPageResult> {
  const functions = getFunctions(app)
  const getPwfActiveTasks = httpsCallable<
    {
      pageSize?: number
      cursor?: PwfActiveTasksCursor | null
    },
    GetPwfActiveTasksResponse
  >(functions, 'getPwfActiveTasks', { timeout: 120_000 })

  try {
    const result = await getPwfActiveTasks({
      pageSize: options?.pageSize ?? ACTIVE_TASKS_PAGE_SIZE,
      cursor: options?.cursor ?? null,
    })
    const data = result.data

    if (!data?.success || !Array.isArray(data.data)) {
      throw new Error('ProWorkflow returned an unexpected response')
    }

    return {
      tasks: data.data.map(mapPwfActiveTask),
      hasMore: data.hasMore ?? false,
      nextCursor: data.nextCursor ?? null,
      recordedAt: data.recordedAt ?? new Date().toISOString(),
    }
  } catch (err: unknown) {
    throw new Error(friendlyPwfError(err, 'getPwfActiveTasks'))
  }
}

export async function fetchActiveTasksPageFromPwf(options?: {
  pageSize?: number
  cursor?: PwfActiveTasksCursor | null
}): Promise<ActiveTasksPageResult> {
  const key = JSON.stringify({
    pageSize: options?.pageSize ?? ACTIVE_TASKS_PAGE_SIZE,
    cursor: options?.cursor ?? null,
  })
  const existing = inFlightPages.get(key)
  if (existing) return existing

  const promise = fetchActiveTasksPageFromPwfUncached(options).finally(() => {
    inFlightPages.delete(key)
  })
  inFlightPages.set(key, promise)
  return promise
}

export async function fetchActiveTasksSummaryFromPwf(): Promise<PwfActiveTasksSummary> {
  const functions = getFunctions(app)
  const getPwfActiveTasksSummary = httpsCallable<
    unknown,
    GetPwfActiveTasksSummaryResponse
  >(functions, 'getPwfActiveTasksSummary', { timeout: 300_000 })

  try {
    const result = await getPwfActiveTasksSummary({})
    const data = result.data

    if (!data?.success || !data.summary) {
      throw new Error('ProWorkflow returned an unexpected summary response')
    }

    return data.summary
  } catch (err: unknown) {
    throw new Error(friendlyPwfError(err, 'getPwfActiveTasksSummary'))
  }
}

/** @deprecated Use fetchActiveTasksPageFromPwf for paginated loading. */
export async function fetchActiveTasksFromPwf(): Promise<{
  tasks: ActiveTaskEntry[]
  recordedAt: string
}> {
  const page = await fetchActiveTasksPageFromPwf({ pageSize: 50 })
  return {
    tasks: page.tasks,
    recordedAt: page.recordedAt,
  }
}
